import lodash from 'lodash'
import { Logger } from 'wdc-cube'
import {
    JunctionMode,
    StringKeywordFilterScope,
    StringKeywordParcelFilterScope,
    StringKeywordOperator,
    type KeywordOption
} from '../tcm_scopes'
import { ValueProperty, StringKeywordFilter } from '../tcm_filter_manager'
import { FilterPaneOwner, FilterPresenter } from './tcm_filter_types'
import { isBlankKeywordOperator } from './tcm_filter_presenter_for_string_multi_select'

const LOG = Logger.get('TCM-KeywordFilter')

let UID_GEN = 0

export class TcmPresenterForStringKeywordFilter extends FilterPresenter<StringKeywordFilterScope, FilterPaneOwner> {
    // Constructor

    constructor(
        owner: FilterPaneOwner,
        scope: StringKeywordFilterScope,
        valueProperty: ValueProperty<StringKeywordFilter>,
        uppercase?: boolean,
        onSearch?: (mode: JunctionMode, text: string) => Promise<KeywordOption[]>
    ) {
        super(owner, scope)
        this.uppercase = uppercase ?? false
        this.valueProperty = valueProperty
        this.onSearch = onSearch
        this.fetch = lodash.debounce(() => {
            this.owner.owner.fetchData('').catch(LOG.caught)
        }, 150)
        this.onSearchDebounce = lodash.debounce(this.doSearch.bind(this), 150)
    }

    // :: Fields

    private readonly uppercase: boolean
    private valueProperty: ValueProperty<StringKeywordFilter>
    private fetch: lodash.DebouncedFuncLeading<() => void>
    private onSearchDebounce: lodash.DebouncedFuncLeading<
        (parcelScope: StringKeywordParcelFilterScope, searchText: string) => void
    >
    private onSearch?: (mode: JunctionMode, text: string) => Promise<KeywordOption[]>

    // :: Getters and Setters

    get owner() {
        return super.owner as FilterPaneOwner
    }

    protected override async bindEvents() {
        this.scope.onAddFilter = this.action(this.handleAddFilter)
        this.scope.onClearAllFilters = this.action(this.handleClearAllFilters)
        LOG.debug('initialized')
    }

    override release(): void {
        this.fetch.cancel()
    }

    override clear() {
        this.scope.conjunction.length = 0
        this.scope.disjunction.length = 0
        this.valueProperty.clear()
    }

    populateUsingValueProporty() {
        this.scope.conjunction.length = 0
        this.scope.disjunction.length = 0

        for (const entry of this.valueProperty.values()) {
            const entryScope = entry.mode === JunctionMode.AND ? this.addConjuntion() : this.addDisjuntion()
            entryScope.operator = entry.operator
            entryScope.value = entry.value ?? ''
            entryScope.valueVisible = !isBlankKeywordOperator(entry.operator)
        }
    }

    override publish() {
        this.valueProperty.clear()

        const emptyPredicate = (scope: StringKeywordParcelFilterScope) =>
            lodash.trim(scope.value) === '' && !isBlankKeywordOperator(scope.operator)
        this.scope.conjunction.removeByCriteria(emptyPredicate)
        this.scope.disjunction.removeByCriteria(emptyPredicate)

        for (const parcelScope of [...this.scope.conjunction, ...this.scope.disjunction]) {
            const isBlank = isBlankKeywordOperator(parcelScope.operator)
            const value = isBlank ? '' : parcelScope.value.trim()
            if (!value && !isBlank) {
                continue
            }
            this.valueProperty.add(
                this.populateWithExtraValues(
                    {
                        mode: parcelScope.junctionMode,
                        operator: parcelScope.operator,
                        value
                    },
                    parcelScope
                )
            )
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected populateWithExtraValues(filter: StringKeywordFilter, _parcelScope: StringKeywordParcelFilterScope) {
        return filter
    }

    addConjuntion() {
        const andScope = new StringKeywordParcelFilterScope()
        andScope.uid = `and-${UID_GEN++}`
        andScope.junctionMode = JunctionMode.AND
        andScope.operator = StringKeywordOperator.is_in
        andScope.onOperatorChange = this.action(this.handleOperatorChange.bind(this, andScope))
        andScope.onValueChange = this.action(this.handleValueChange.bind(this, andScope))
        andScope.onClear = this.action(this.handleClear.bind(this, andScope))
        andScope.onEnterKeyPressed = this.action(this.handleEnterKeyPressed)
        andScope.onOptionSelected = this.action(this.handleOptionSelected.bind(this, andScope))
        andScope.update = this.update
        this.scope.conjunction.push(andScope)
        return andScope
    }

    addDisjuntion() {
        const orScope = new StringKeywordParcelFilterScope()
        orScope.uid = `or-${UID_GEN++}`
        orScope.junctionMode = JunctionMode.OR
        orScope.operator = StringKeywordOperator.is_in
        orScope.onOperatorChange = this.action(this.handleOperatorChange.bind(this, orScope))
        orScope.onValueChange = this.action(this.handleValueChange.bind(this, orScope))
        orScope.onClear = this.action(this.handleClear.bind(this, orScope))
        orScope.onEnterKeyPressed = this.action(this.handleEnterKeyPressed)
        orScope.onOptionSelected = this.action(this.handleOptionSelected.bind(this, orScope))
        orScope.update = this.update
        this.scope.disjunction.push(orScope)
        return orScope
    }

    getsIn() {
        for (const conditionScope of this.scope.conjunction) {
            if (conditionScope.operator === StringKeywordOperator.is_in) {
                return conditionScope
            }
        }

        for (const conditionScope of this.scope.disjunction) {
            if (conditionScope.operator === StringKeywordOperator.is_in) {
                return conditionScope
            }
        }

        return null
    }

    getOrCreateIsIn() {
        let conditioScope = this.getsIn()
        if (conditioScope) {
            return conditioScope
        }

        conditioScope = this.addConjuntion()
        conditioScope.operator = StringKeywordOperator.is_in
        return conditioScope
    }

    private async handleClearAllFilters() {
        this.clear()
    }

    private async handleAddFilter(mode: JunctionMode) {
        this.mostrarItemAdicionado()

        if (mode === JunctionMode.AND) {
            this.addConjuntion()
        } else {
            this.addDisjuntion()
        }
    }

    private async handleClear(parcelScope: StringKeywordParcelFilterScope) {
        let found = true

        let idx = this.scope.conjunction.findIndex((item) => item.uid === parcelScope.uid)
        if (idx !== -1) {
            this.scope.conjunction.removeByIndex(idx)
            found = true
        }

        idx = this.scope.disjunction.findIndex((item) => item.uid === parcelScope.uid)
        if (idx !== -1) {
            this.scope.disjunction.removeByIndex(idx)
            found = true
        }

        if (found && parcelScope.value) {
            this.fetch.cancel()
            this.fetch()
        }
    }

    private async handleOperatorChange(parcelScope: StringKeywordParcelFilterScope, value: StringKeywordOperator) {
        parcelScope.operator = value
        parcelScope.valueVisible = !isBlankKeywordOperator(value)
    }

    private async handleValueChange(parcelScope: StringKeywordParcelFilterScope, value: string) {
        if (this.uppercase) {
            parcelScope.value = value.toUpperCase()
        } else {
            parcelScope.value = value
        }

        if (this.onSearch) {
            this.onSearchDebounce.cancel()
            this.onSearchDebounce(parcelScope, parcelScope.value.trim())
        }
    }

    private async handleOptionSelected(parcelScope: StringKeywordParcelFilterScope, value: KeywordOption) {
        parcelScope.value = value.key
        parcelScope.options = []
    }

    private async handleEnterKeyPressed() {
        this.fetch.cancel()
        this.fetch()
    }

    private doSearch(parcelScope: StringKeywordParcelFilterScope, searchText: string) {
        if (!this.onSearch) {
            parcelScope.options.length > 0 && (parcelScope.options = [])
            return
        }

        if (searchText) {
            this.onSearch(parcelScope.junctionMode, searchText)
                .then((options) => {
                    parcelScope.options = options
                })
                .catch((e) => {
                    parcelScope.options = []
                    LOG.error(e.message)
                })
            return
        }

        parcelScope.options.length > 0 && (parcelScope.options = [])
    }

    apply(filters: StringKeywordFilter[] | null | undefined) {
        if (!filters || filters.length === 0) {
            return
        }

        filters.forEach((filter) => {
            const isBlank = isBlankKeywordOperator(filter.operator)
            if (!filter.value && !isBlank) {
                return
            }

            let itemScope: StringKeywordParcelFilterScope
            if (filter.mode === JunctionMode.AND) {
                itemScope = this.addConjuntion()
            } else {
                itemScope = this.addDisjuntion()
            }

            itemScope.value = isBlank ? '' : (filter.value ?? '')
            itemScope.operator = filter.operator
            itemScope.valueVisible = !isBlank
        })
    }
}
